import { computed, effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService, WINDOW } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NgxPermissionsService } from 'ngx-permissions';
import { switchMap } from 'rxjs';

import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@kr/core';
import { environment } from '@kr/environment';
import { GetTotalBalanceReq, Provider } from '@kr/models';
import { LoginType } from '@kr/src/models';
import { AUTH_EXPIRE_KEY, DictionariesStore, REFRESH_EXPIRE_KEY } from '@kr/store';

import { SHELL_API } from './api';
import { ShellState } from './models';
import { getPermissionsArray } from '../init/app-permissions.guard';

const initialState: ShellState = {
  i18Header: '',
  account: null,
  totalBalances: [],
  supportLink: null,
};

export const ShellStore = signalStore(
  withDevtools('shell'),
  withState(initialState),
  withRequestStatus(['getAccount', 'getTotalBalances', 'getTotalBalance', 'getSupportLink']),
  withRequestHandler(),
  withProps(store => ({
    store,
    shellApi: inject(SHELL_API),
    storeHelperService: inject(StoreHelperService),
    dictionariesStore: inject(DictionariesStore),
    spinnerService: inject(SpinnerService),
    window: inject<Window>(WINDOW),
    permissionsService: inject(NgxPermissionsService),
  })),
  withComputed(({ dictionariesStore }) => ({
    _companyList: computed(() => dictionariesStore.companyList()),
  })),
  withMethods(({ store, window, shellApi, permissionsService }) => ({
    getAccount: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getAccount'));
        return shellApi.getAccount().pipe(
          tapResponse({
            next: account => {
              patchState(store, { account }, setSuccessStatusFor('getAccount'));

              if (environment.loginType === LoginType.External) {
                permissionsService.loadPermissions(getPermissionsArray(account.groups));
              }
              window.localStorage.setItem(AUTH_EXPIRE_KEY, account.accessExpire.toString());
              window.localStorage.setItem(REFRESH_EXPIRE_KEY, account.refreshExpire.toString());
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getAccount', error)),
          }),
        );
      }),
    ),
    getTotalBalances: rxMethod<Provider[]>(
      switchMap(payload => {
        patchState(store, setPendingStatusFor('getTotalBalances'));
        return shellApi.getTotalBalances(payload).pipe(
          tapResponse({
            next: totalBalances => {
              patchState(store, { totalBalances }, setSuccessStatusFor('getTotalBalances'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getTotalBalances', error)),
          }),
        );
      }),
    ),
    getSupportLink: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getSupportLink'));
        return shellApi.getSupportLink().pipe(
          tapResponse({
            next: supportLink => {
              window.open(supportLink.url, '_blank');
              patchState(store, { supportLink }, setSuccessStatusFor('getSupportLink'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getSupportLink', error)),
          }),
        );
      }),
    ),
    getTotalBalance: rxMethod<{ params: GetTotalBalanceReq; provider: Provider }>(
      switchMap(({ params, provider }) => {
        patchState(store, setPendingStatusFor('getTotalBalance'));
        return shellApi.getTotalBalance(params, provider).pipe(
          tapResponse({
            next: balance => {
              patchState(
                store,
                {
                  totalBalances: store.totalBalances().map(b => (b.provider.id === provider.id ? balance : b)),
                },
                setSuccessStatusFor('getTotalBalance'),
              );
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getTotalBalance', error)),
          }),
        );
      }),
    ),
  })),
  withMethods(({ store, getAccount, getTotalBalances, _companyList, dictionariesStore }) => ({
    setHeader: (i18Header: string) => {
      patchState(store, { i18Header });
    },
    initialize: () => {
      getAccount();
      dictionariesStore.getAvailableCompanies();
      dictionariesStore.getCurrencies();
    },
    _getTotalBalances: () => {
      if (_companyList().length) {
        const providers = _companyList().find(company => company.id === dictionariesStore.getStoredOrgId())?.organizations;
        providers && getTotalBalances(providers);
      }
    },
  })),
  withHooks({
    onInit(store) {
      store.initialize();
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
      effect(() => {
        if (store._companyList().length && +store.dictionariesStore.getStoredOrgId()) {
          store._getTotalBalances();
        }
      });
    },
    onDestroy(store) {
      store.dictionariesStore.clearStoreData();
    },
  }),
);
